import {RefObject, useCallback, useLayoutEffect, useRef, useState} from "react";

interface TooltipPosition {
    top: number;
    left: number;
    placement: "top" | "bottom";
}

interface UseDayTooltipProps {
    getDayTooltip?: (date: Date) => string | null | undefined;
}

interface UseDayTooltipReturn {
    tooltip: string | null;
    tooltipPosition: TooltipPosition | null;
    containerRef: RefObject<HTMLDivElement | null>;
    tooltipRef: RefObject<HTMLDivElement | null>;
    showTooltip: (date: Date, element: HTMLDivElement) => void;
    hideTooltip: () => void;
    toggleTooltip: (date: Date, element: HTMLDivElement) => void;
}

export function useDayTooltip({getDayTooltip}: UseDayTooltipProps): UseDayTooltipReturn {
    const [tooltip, setTooltip] = useState<string | null>(null);
    const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const elementRef = useRef<HTMLDivElement | null>(null);

    // Recalculate position after tooltip renders to check bounds
    useLayoutEffect(() => {
        if (!tooltip || !tooltipRef.current || !tooltipPosition || !containerRef.current || !elementRef.current) return;

        const tooltipEl = tooltipRef.current;
        const containerRect = containerRef.current.getBoundingClientRect();
        const elementRect = elementRef.current.getBoundingClientRect();

        // Get tooltip dimensions
        const tooltipWidth = tooltipEl.offsetWidth;
        const tooltipHeight = tooltipEl.offsetHeight;

        // Calculate tooltip center position relative to container
        const elementCenterX = elementRect.left - containerRect.left + elementRect.width / 2;

        // Calculate left position so tooltip is centered on element
        let newLeft = elementCenterX;

        // Check if tooltip goes beyond container right edge
        const tooltipRight = elementCenterX + tooltipWidth / 2;
        if (tooltipRight > containerRect.width) {
            newLeft = containerRect.width - tooltipWidth / 2 - 5;
        }

        // Check if tooltip goes beyond container left edge
        const tooltipLeft = elementCenterX - tooltipWidth / 2;
        if (tooltipLeft < 0) {
            newLeft = tooltipWidth / 2 + 5;
        }

        // Check vertical placement
        let newPlacement = tooltipPosition.placement;
        const elementTop = elementRect.top - containerRect.top;

        // If tooltip would go above container, place it below
        if (newPlacement === "top" && elementTop < tooltipHeight + 10) {
            newPlacement = "bottom";
        }

        if (newLeft !== tooltipPosition.left || newPlacement !== tooltipPosition.placement) {
            setTooltipPosition((prev) => (prev ? {...prev, left: newLeft, placement: newPlacement} : null));
        }
    }, [tooltip, tooltipPosition]);

    const showTooltip = useCallback(
        (date: Date, element: HTMLDivElement) => {
            if (!getDayTooltip) return;

            const tooltipText = getDayTooltip(date);
            if (tooltipText) {
                setTooltip(tooltipText);
                elementRef.current = element;

                // Calculate position relative to container
                const elementRect = element.getBoundingClientRect();
                const containerRect = containerRef.current?.getBoundingClientRect();

                if (containerRect) {
                    const top = elementRect.top - containerRect.top;
                    const left = elementRect.left - containerRect.left + elementRect.width / 2;

                    // Default placement is top, will be adjusted in useLayoutEffect if needed
                    setTooltipPosition({
                        top,
                        left,
                        placement: "top",
                    });
                }
            } else {
                setTooltip(null);
                setTooltipPosition(null);
                elementRef.current = null;
            }
        },
        [getDayTooltip],
    );

    const hideTooltip = useCallback(() => {
        setTooltip(null);
        setTooltipPosition(null);
        elementRef.current = null;
    }, []);

    const toggleTooltip = useCallback(
        (date: Date, element: HTMLDivElement) => {
            if (tooltip) {
                hideTooltip();
            } else {
                showTooltip(date, element);
            }
        },
        [tooltip, hideTooltip, showTooltip],
    );

    return {
        tooltip,
        tooltipPosition,
        containerRef,
        tooltipRef,
        showTooltip,
        hideTooltip,
        toggleTooltip,
    };
}
